import { Buffer } from 'buffer';
import { selectDistanceOptions } from 'common/hooks/useSelectOptions';
import { BodyTypeValues } from 'common/types/body-type';
import { ColorTypeValues } from 'common/types/color-type';
import { ConditionTypeValues } from 'common/types/condition-type';
import { CylinderTypeValuesAbbrev } from 'common/types/cylinder-type';
import { DoorTypeValues } from 'common/types/door-type';
import { DrivetrainTypeValues } from 'common/types/drivetrain-type';
import { FuelTypeValues } from 'common/types/fuel-type';
import { MileageRange, MileageRangeValues } from 'common/types/mileage-range';
import { MakeModelsParams, SearchParams, searchParamsInitialState } from 'common/types/SearchParams';
import { TitleStatusValues } from 'common/types/title-status';
import { TransmissionTypeValues } from 'common/types/transmission-type';
import { BodyType, CylinderType, DoorType, DrivetrainType, FuelType, TransmissionType } from 'graphql/generated';
import _ from 'lodash';
import { ZipCodeObject } from 'redux/search-param.slice';

import { formatters } from './formatters';

export const customParams = {
  BooleanParam: (string = '') => string === 'true',
  NumberParam: (string = '') => (string ? Number(string) : null),
  // other transformation functions to map all data types
};

const addStrArrayUrlParam = (urlParams: URLSearchParams, key: string, vals?: string[]) => {
  for (const val of vals || []) {
    urlParams.append(key, encodeURIComponent(val));
  }
};

const addLiteralUrlParam = (urlParams: URLSearchParams, key: string, val?: string | number | boolean) => {
  if (val) {
    urlParams.append(key, encodeURIComponent(val));
  }
};

const addZcoUrlParam = (urlParams: URLSearchParams, val?: ZipCodeObject) => {
  if (val) {
    urlParams.append('zc', encodeURIComponent(val.code));
    urlParams.append('la', encodeURIComponent(val.latitude));
    urlParams.append('lo', encodeURIComponent(val.longitude));
  }
};

const addMakeModelUrlParam = (urlParams: URLSearchParams, val?: MakeModelsParams[]) => {
  if (val) {
    for (let i = 0; i < val.length; i++) {
      addLiteralUrlParam(urlParams, `m${i.toString()}`, val[i].make);
      addStrArrayUrlParam(urlParams, `mo${i.toString()}`, val[i].models);
    }
  }
};

const getSearchParamsObject = (urlParams: URLSearchParams) => {
  const obj: any = {};
  for (const key of [...new Set([...urlParams.keys()])]) {
    const vals = urlParams.getAll(key);
    if (vals.length > 1) obj[key] = vals;
    else obj[key] = vals[0];
  }
  return obj;
};

const parseArray = (val?: string | string[]) => {
  if (!val) return undefined;
  if (Array.isArray(val)) return val;
  return [val];
};

const parseZco = (obj: any) => {
  if (!obj.zc || !obj.la || !obj.lo) return undefined;
  return {
    code: obj.zc,
    latitude: +obj.la,
    longitude: +obj.lo,
  };
};

const parseModels = (models: string[], obj: any, idx = 0): string[] => {
  if (obj[`mo${idx.toString()}`]) {
    models.push(obj[`mo${idx.toString()}`]);
    return parseModels(models, obj, idx + 1);
  } else return models;
};

const parseMakes = (obj?: any, makeModels?: MakeModelsParams[], idx = 0): MakeModelsParams[] | undefined => {
  if (!obj) return undefined;
  if (obj[`m${idx.toString()}`]) {
    const mm: MakeModelsParams = {
      make: obj[`m${idx.toString()}`],
      models: parseModels([], obj),
    };
    if (!makeModels) makeModels = [mm];
    else makeModels.push(mm);
    return parseMakes(obj, makeModels, idx + 1);
  } else return makeModels;
};

const toSearchParams = (urlParams?: URLSearchParams): SearchParams => {
  if (!urlParams?.get('params')) return searchParamsInitialState;

  const buffer = Buffer.from(urlParams.get('params')!, 'base64');
  const json = buffer.toString('utf-8');
  const obj = JSON.parse(json);
  // const params: SearchParams = {
  //   ...obj,
  // };
  // console.log('searchParams', obj);
  return obj;
  // // const obj = getSearchParamsObject(urlParams);

  // const searchParams: SearchParams = {
  //   ...searchParamsInitialState,
  //   zipCodeObj: parseZco(obj),
  //   ...(obj.dst && { distance: obj.dst }),
  //   ...(obj.bt && { bodys: parseArray(obj.bt) }),
  //   ...(obj.ts && { titleSs: parseArray(obj.ts) }),
  //   ...(obj.clt && { colors: parseArray(obj.clt) }),
  //   ...(obj.cnt && { conditions: parseArray(obj.cnt) }),
  //   ...(obj.cyt && { cylinders: parseArray(obj.cyt) }),
  //   ...(obj.drt && { doors: parseArray(obj.drt) }),
  //   ...(obj.dvt && { drivetrains: parseArray(obj.dvt) }),
  //   ...(obj.ft && { fuels: parseArray(obj.ft) }),
  //   ...(obj.trt && { transmissions: parseArray(obj.trt) }),
  //   ...(obj.ml && { mileage: obj.ml }),
  //   ...(obj.yx && { yearMax: obj.yx }),
  //   ...(obj.yn && { yearMin: obj.yn }),
  //   ...(obj.px && { priceMax: obj.px }),
  //   ...(obj.pn && { priceMin: obj.pn }),
  //   ...(obj.p !== undefined && { page: +obj.p }),
  //   ...(obj.rp !== undefined && { rowsPP: +obj.rp }),
  //   ...(obj.s && { sortIdx: obj.s }),
  //   ...(obj.m0 && { makeModels: parseMakes(obj) }),
  // };
  // if (!searchParams.zipCodeObj) {
  //   console.log('set error', searchParams);
  //   delete searchParams.zipCodeObj;
  //   searchParams.error = 1;
  // }
  // // console.log('-->', searchParams);
  // return searchParams;
};

const toUrlParams = (searchParams: SearchParams) => {
  const json = JSON.stringify(searchParams);
  const buffer = Buffer.from(json);

  const urlParams: URLSearchParams = new URLSearchParams();
  urlParams.append('params', buffer.toString('base64'));
  return urlParams.toString();
};

const toSaveJson = (searchParams: SearchParams) => {
  const { page, rowsPP, sortIdx, ...params } = searchParams;
  const ret = JSON.stringify(params);
  // console.log(ret);
  return ret;
};

const arrayDesc = (typeLabel: string, values: string | string[] | undefined, labels: { [key: string]: string }) => {
  if (!values) return '';
  if (!Array.isArray(values)) return `, ${typeLabel}=${labels[values]}`;
  if (values.length === 1) return `, ${typeLabel}=${labels[values[0]]}`;
  else return `, ${typeLabel}=[${values.map(x => labels[x]).join(`, `)}]`;
};

const toFriendlyName = (searchParams: SearchParams) => {
  let name = `${
    selectDistanceOptions.find(x => x.id === searchParams.distance?.toString())!.label
  } ${searchParams.zipCodeObj?.code.toString()}`;
  for (const make of searchParams.makeModels || []) {
    name += `, Make=${make.make}`;
    if (make.models.length === 1) name += ` & Model=${make.models[0]}`;
    if (make.models.length > 1) name += ` & Models=[${make.models.join(', ')}]`;
  }
  if (searchParams.mileage) name += `, ${MileageRangeValues[searchParams.mileage as MileageRange]} Miles`;
  if (searchParams.yearMin) name += `, Min-Year: ${searchParams.yearMin}`;
  if (searchParams.yearMax) name += `, Max-Year: ${searchParams.yearMax}`;
  if (searchParams.priceMin) name += `, Min-Price: ${formatters.currency.format(+searchParams.priceMin)}`;
  if (searchParams.priceMax) name += `, Max-Price: ${formatters.currency.format(+searchParams.priceMax)}`;

  name += arrayDesc('BodyType', searchParams.bodys, BodyTypeValues);
  name += arrayDesc('TitleStatus', searchParams.titleSs, TitleStatusValues);
  name += arrayDesc('Color', searchParams.colors, ColorTypeValues);
  name += arrayDesc('Doors', searchParams.doors, DoorTypeValues);
  name += arrayDesc('Condition', searchParams.conditions, ConditionTypeValues);
  name += arrayDesc('Drivetrain', searchParams.drivetrains, DrivetrainTypeValues);
  name += arrayDesc('Cylinders', searchParams.cylinders, CylinderTypeValuesAbbrev);
  name += arrayDesc('Fuel', searchParams.fuels, FuelTypeValues);
  name += arrayDesc('Transmission', searchParams.transmissions, TransmissionTypeValues);

  //   ...(obj.m0 && { makeModels: parseMakes(obj) }),

  return name;
};

const dbToUrlParams = (json: string) => {
  const obj = JSON.parse(json);
  const searchParams: SearchParams = {
    ...searchParamsInitialState,
    ...obj,
  };
  return toUrlParams(searchParams);
};

export const searchParamTools = {
  toSearchParams,
  addStrArrayUrlParam,
  addLiteralUrlParam,
  addMakeModelUrlParam,
  toSaveJson,
  toUrlParams,
  dbToUrlParams,
  toFriendlyName,
};
